import { safeParseJSON } from './safe-parse-json.util';

// Map of tags to insight keys
const TAG_TO_INSIGHT_KEY_MAP: { [key: string]: string } = {
  'ASK_ASSISTANT_ABOUT_COMPANY': 'about_company',
  'ASK_ASSISTANT_COMPANY_REVENUE': 'company_revenue',
  'ASK_ASSISTANT_SPECIAL_BOX': 'special_box',
  'ASK_ASSISTANT_SALES_INSIGHTS': 'sales_insights',
  'ASK_ASSISTANT_PERSONAL_BACKGROUND': 'personal_background',
  'ASK_ASSISTANT_INITIAL_OUTREACH': 'initial_outreach',
  'ASK_ASSISTANT_BASIC_WORDS_IN_LOCAL_LANGUAGE': 'basic_words',
  'ASK_ASSISTANT_C_LEVEL': 'c_level',
  'ASK_ASSISTANT_COMPANY_NEWS': 'company_news',
  'ASK_ASSISTANT_EMAIL_SEQUENCE': 'email_sequence',
  'ASK_ASSISTANT_LINKEDIN_SEQUENCE': 'linkedin_sequence',
  'ASK_ASSISTANT_EDUCATION_INSIGHT': 'education_insight',
  'ASK_ASSISTANT_ADVANCED_CAREER': 'advanced_career',
  'ASK_ASSISTANT_DEPARTMENT_YEARS': 'department_years',
  'ASK_ASSISTANT_EXPERTISE_AREAS': 'expertise_areas',
  'ASK_ASSISTANT_EXPLORATORY_EMAIL_EXAMPLE': 'exploratory_email_example',
  'ASK_ASSISTANT_INDUSTRY_CHALLENGES': 'industry_challenges',
  'ASK_ASSISTANT_INSIGHTS_ABOUT_CAREER': 'insights_about_career',
  'ASK_ASSISTANT_MANAGEMENT_EXPERIENCE': 'management_experience',
  'ASK_ASSISTANT_MEETING_AGENDA': 'meeting_agenda',
  'ASK_ASSISTANT_TOTAL_BUSINESS_TENURE': 'total_business_tenure',
  'ASK_ASSISTANT_YEARS_IN_CURRENT_COMPANY': 'years_in_current_company'
};

/**
 * Get assistant prompt by tag.
 *
 * @param company The company object containing assistant_prompts or insights.
 * @param tag The tag of the assistant prompt to retrieve.
 * @returns The content of the assistant prompt or null if not found.
 */
export function getAssistantPromptByTag(company: any, tag: string): any {
  // Check if company exists
  if (!company) {
    return null;
  }

  // First, try the new insights structure
  if (company.insights && typeof company.insights === 'object') {
    // Get the insight key from the tag
    const insightKey = TAG_TO_INSIGHT_KEY_MAP[tag];

    if (insightKey && company.insights[insightKey]) {
      const insight = company.insights[insightKey];

      // Get the response content
      let content = insight.response;

      // Handle the response structure similar to how we handle prompt.content
      if (content) {
        // If response has message.content structure (like initial_outreach)
        if (content?.message?.content) {
          content = content.message.content;
        }

        // console.log(`[getAssistantPromptByTag] Content type from insights:`, typeof content);

        // If content is already an object, return it directly
        if (typeof content === 'object' && content !== null) {
          // console.log(`[getAssistantPromptByTag] Returning object content from insights:`, JSON.stringify(content).slice(0, 100) + '...');
          return content;
        }

        // If content is a string that looks like JSON with short/long properties
        if (typeof content === 'string' &&
          content.trim().startsWith('{') &&
          (content.includes('"short"') || content.includes("'short'"))) {
          try {
            const parsed = JSON.parse(content);
            if (typeof parsed === 'object' && parsed !== null) {
              // console.log(`[getAssistantPromptByTag] Successfully parsed JSON string from insights for ${tag}`);
              return parsed;
            }
          } catch (e) {
            console.error(`[getAssistantPromptByTag] Error parsing JSON from insights for ${tag}:`, e);
            // Return the original string if parsing fails
          }
        }

        // Return the content as is
        // console.log(`[getAssistantPromptByTag] Returning original content from insights:`, typeof content === 'string' ? content.slice(0, 100) + '...' : content);
        return content;
      }
    }
  }

  // Fallback to the old assistant_prompts structure
  if (!company.assistant_prompts || !Array.isArray(company.assistant_prompts)) {
    return null;
  }

  // Find the assistant prompt by the tag (original logic)
  const prompt = company.assistant_prompts.find((item: any) => item.tag === tag);

  // If prompt not found, return null
  if (!prompt) {
    return null;
  }

  // console.log(`[getAssistantPromptByTag] Found prompt for tag: ${tag}, content type:`, typeof prompt.content);

  // If content is already an object, return it directly
  if (typeof prompt.content === 'object' && prompt.content !== null) {
    // console.log(`[getAssistantPromptByTag] Returning object content:`, JSON.stringify(prompt.content).slice(0, 100) + '...');
    return prompt.content;
  }

  // If content is a string that looks like JSON with short/long properties
  if (typeof prompt.content === 'string' &&
    prompt.content.trim().startsWith('{') &&
    (prompt.content.includes('"short"') || prompt.content.includes("'short'"))) {
    try {
      const parsed = JSON.parse(prompt.content);
      if (typeof parsed === 'object' && parsed !== null) {
        // console.log(`[getAssistantPromptByTag] Successfully parsed JSON string for ${tag}`);
        return parsed;
      }
    } catch (e) {
      console.error(`[getAssistantPromptByTag] Error parsing JSON for ${tag}:`, e);
      // Return the original string if parsing fails
    }
  }

  // Return the original content as is
  // console.log(`[getAssistantPromptByTag] Returning original content:`, typeof prompt.content === 'string' ? prompt.content.slice(0, 100) + '...' : prompt.content);
  return prompt.content;
}
